import { yupResolver } from "@hookform/resolvers/yup"
import classNames from "classnames"
import React from "react"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import { useSettings } from "../../../../../aggregators/rootData"
import {
  CalendarEventDataFragment,
  EventRegistrationErrorType,
} from "../../../../../api/backend/backend-api"
import CustomAlert from "../../../../../ui/atoms/Alert"
import ErrorLabel from "../../../../../ui/atoms/ErrorLabel"
import Typography from "../../../../../ui/atoms/Typography"
import { EditorialLabel } from "../../../../contents"
import { useLabels } from "../../../../contents/hooks"
import ReCaptchaControl from "../../../../recaptcha"
import classes from "./EventCalendarForm.module.css"
import EventCalendarSubmitButton from "./EventCalendarSubmitButton"

type FieldType = "string" | "text" | "dropdown"
interface FormField {
  autoComplete?: string
  name: string
  required?: boolean
  type: FieldType
  inputType?: string
  label: string
  options?: string[]
}

const createSchemaField = (field: FormField) => {
  let f = yup.string()
  if (field.inputType === "email") {
    f = f.email()
  }
  if (field.required) {
    f = f.required()
  }
  return f
}

const createSchema = (fields: FormField[]) => {
  const fieldsSchema = {} as any
  for (const field of fields) {
    fieldsSchema[field.name] = createSchemaField(field)
  }
  return yup.object().shape(fieldsSchema as any)
}

interface Props {
  calendar: CalendarEventDataFragment
  onSubmit: (data: any) => void
  submitting: boolean
  submitError: boolean
  submitErrorType?: EventRegistrationErrorType
}

const EventCalendarForm = ({
  calendar,
  onSubmit,
  submitting,
  submitError,
  submitErrorType,
}: Props) => {
  const { getLabel } = useLabels()
  const settings = useSettings()

  const fields: FormField[] = [
    {
      name: "name",
      autoComplete: "name",
      required: true,
      type: "string",
      label: getLabel("calendar", "form-name"),
    },
    {
      name: "email",
      autoComplete: "email",
      required: true,
      type: "string",
      inputType: "email",
      label: getLabel("calendar", "form-email"),
    },
    ...(calendar.definition.extraQuestions.map((x: any) => ({
      name: x.key,
      required: x.required,
      type: x.type,
      label: x.label,
      options: x.options,
    })) ?? []),
  ]
  const schema = createSchema(fields)
  const { register, errors, handleSubmit } = useForm({
    resolver: yupResolver(schema),
  })

  return (
    <div className={classes.root}>
      <EditorialLabel
        className="mb-4"
        scope="calendar"
        id="form-title"
        variant="subtitle2"
      />

      {fields.map((x) => (
        <div className={classes.formControl} key={x.name}>
          <Typography variant="body1" className={classes.label}>
            {x.label} {x.required ? "*" : ""}
          </Typography>
          {x.type === "string" && (
            <input
              ref={register}
              name={x.name}
              autoComplete={x.autoComplete}
              type={x.inputType}
              className={classNames(classes.input, {
                [classes.inputError]: !!errors[x.name],
              })}
            />
          )}
          {x.type === "text" && (
            <textarea
              ref={register}
              rows={3}
              name={x.name}
              autoComplete={x.autoComplete}
              className={classNames(classes.input, {
                [classes.inputError]: !!errors[x.name],
              })}
            />
          )}
          {x.type === "dropdown" && (
            <select
              ref={register}
              name={x.name}
              autoComplete={x.autoComplete}
              className={classNames(classes.input, {
                [classes.inputError]: !!errors[x.name],
              })}
            >
              {x.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}
          <ErrorLabel
            error={!!errors[x.name]}
            message={getLabel("forms", "invalid-field")}
            className="mt-2"
          />
        </div>
      ))}

      <div className="mt-4">
        <ReCaptchaControl
          disabled={settings?.calendars?.recaptchaEnabled !== true}
        >
          <EventCalendarSubmitButton
            loading={submitting}
            onSubmit={handleSubmit(onSubmit)}
          />
        </ReCaptchaControl>
      </div>
      <CustomAlert
        show={
          submitError &&
          (!submitErrorType ||
            submitErrorType === EventRegistrationErrorType.GenericError)
        }
        severity="error"
      >
        {getLabel("messages", "generic-error")}
      </CustomAlert>
      <CustomAlert
        show={
          submitErrorType === EventRegistrationErrorType.InvalidWorkEmailDomain
        }
        severity="warning"
      >
        {getLabel("calendar", "invalid-work-domain")}
      </CustomAlert>
      <CustomAlert
        show={submitErrorType === EventRegistrationErrorType.AlreadyRegistered}
        severity="warning"
      >
        {getLabel("calendar", "already-registered")}
      </CustomAlert>
      <CustomAlert
        show={submitErrorType === EventRegistrationErrorType.BlockedEmailDomain}
        severity="warning"
      >
        {getLabel("calendar", "blocked-email-domain")}
      </CustomAlert>
    </div>
  )
}

export default EventCalendarForm
